import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { openRazorpayCheckout, fetchPaymentStatus } from '@/lib/payments';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Check, Crown, Zap, Star, Infinity as InfinityIcon, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Hint, InfoDot } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';

const PLAN_HELP = { monthly: HELP.subscription.monthly, yearly: HELP.subscription.yearly, lifetime: HELP.subscription.lifetime };

const TIER_STYLES = {
  basic:   { gradient: 'from-slate-50 to-slate-100',   border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600',  icon: Zap,   accent: 'text-slate-700' },
  growth:  { gradient: 'from-blue-50 to-indigo-50',    border: 'border-blue-300',  badge: 'bg-blue-100 text-blue-700',     icon: Star,  accent: 'text-blue-600' },
  premium: { gradient: 'from-amber-50 to-orange-50',   border: 'border-amber-300', badge: 'bg-amber-100 text-amber-700',   icon: Crown, accent: 'text-amber-600' },
};

const PLAN_KEY_BY_TIER = { growth: 'growth_monthly', premium: 'elite_monthly' };

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [currentTier, setCurrentTier] = useState('basic');
  const [paymentsOn, setPaymentsOn] = useState(false);
  const [lifetimeOffer, setLifetimeOffer] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  // Annual is the default view — most students save two months on it.
  const [cycle, setCycle] = useState('yearly');
  useScreenTip('subscription', HELP.tips.subscription);

  useEffect(() => {
    apiGet('/subscription/tiers').then(d => setTiers(d.tiers)).catch(() => {});
    apiGet('/subscription/current').then(d => setCurrentTier(d.tier)).catch(() => {});
    apiGet('/public/settings').then(d => {
      setPaymentsOn(d?.feature_toggles?.payments_enabled ?? false);
      const pc = d?.payment_config || {};
      setLifetimeOffer({
        enabled: !!pc.lifetime_offer_enabled,
        url: pc.lifetime_offer_url || '',
        label: pc.lifetime_offer_label || '₹5,999 - Lifetime Offer',
        note: pc.lifetime_offer_note || 'Independence Day Offer · Available for the first 499 students only — Grab The Offer Today!',
        tier: pc.lifetime_offer_tier || 'premium',
        displayPrice: pc.elite_display_price_inr ?? 999,
        displayPeriod: pc.elite_display_period || 'month',
      });
    }).catch(() => {});
    fetchPaymentStatus().then(setPaymentStatus).catch(() => {});
  }, []);

  if (!tiers) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderableTiers = Object.entries(tiers).filter(([k, t]) => (t.tier_status || 'visible') !== 'hidden' && k !== 'basic' && k !== 'basic_lifetime');
  const trialActive = paymentStatus?.trial_active;
  const trialEnd = paymentStatus?.trial_end_label || paymentStatus?.trial_end_date;
  const lifetimePrice = paymentStatus?.basic_lifetime_price_inr ?? 49;
  const lifetimeLabel = paymentStatus?.basic_lifetime_label ?? 'Basic Lifetime — Pay Once';
  const trialBanner = paymentStatus?.trial_banner_copy;
  const isLifetime = (currentTier === 'basic_lifetime') || (user?.subscription_tier === 'basic_lifetime');

  const startCheckout = async (planKey) => {
    setLoadingPlan(planKey);
    // Decision 5a — if the user is already on a paid tier (growth, premium,
    // or basic_lifetime), the appropriate flow is the upgrade endpoint with
    // a 1-month loyalty bonus. /create-subscription is for first-time paid
    // sign-ups from Basic-free. This logic only applies to recurring plans;
    // one-time lifetime upgrades still flow through /create-order via
    // openRazorpayCheckout's normal path.
    const isRecurring = planKey.endsWith('_monthly') || planKey.endsWith('_yearly');
    const isPayingCustomer = (
      ['growth', 'premium'].includes((currentTier || '').toLowerCase())
      || isLifetime
      || user?.subscription_active
    );
    if (isRecurring && isPayingCustomer) {
      try {
        const r = await apiPost('/subscriptions/upgrade', { target_plan_key: planKey });
        // Hand the returned subscription_id straight to Razorpay Checkout
        // in subscription mode — same UX as a fresh sign-up, but the loyalty
        // bonus is baked into the start_at on Razorpay's side.
        if (r.is_mock || !r.key_id) {
          toast.error('Recurring payments not yet configured');
        } else if (typeof window !== 'undefined' && window.Razorpay) {
          await new Promise((resolve) => {
            const rzp = new window.Razorpay({
              key: r.key_id,
              subscription_id: r.subscription_id,
              name: 'Gurukul Veda',
              description: r.plan_label,
              prefill: { name: r.user?.name || '', email: r.user?.email || '' },
              theme: { color: '#6366F1' },
              handler: async () => {
                const bonusMsg = r.loyalty_bonus_days > 0
                  ? ` Your first ${r.loyalty_bonus_days} days are on us — first charge on ${new Date(r.first_charge_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}.`
                  : '';
                toast.success(`Upgrade scheduled.${bonusMsg}`);
                await refreshUser?.();
                resolve();
              },
              modal: { ondismiss: () => { toast.error('Checkout closed before mandate set up'); resolve(); } },
            });
            rzp.open();
          });
        }
      } catch (e) {
        toast.error(e?.message || 'Upgrade failed');
      }
      setLoadingPlan(null);
      return;
    }
    const res = await openRazorpayCheckout(planKey, {
      onSuccess: async (v) => {
        if (v?.recurring) {
          toast.success('Subscription set up — your tier will activate within a minute.');
        } else {
          toast.success(`Payment successful — you're now on ${v.new_tier === 'basic_lifetime' ? 'Basic Lifetime' : v.new_tier}.`);
        }
        await refreshUser?.();
        const fresh = await fetchPaymentStatus();
        setPaymentStatus(fresh);
        if (v?.new_tier) setCurrentTier(v.new_tier);
      },
      onFailure: (msg) => msg && msg !== 'Cancelled' && toast.error(msg),
    });
    setLoadingPlan(null);
    return res;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12" data-testid="subscription-page">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="sub-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Choose Your Plan</h1>
          <p className="text-sm text-slate-500 font-body">Unlock more features as you grow</p>
        </div>
        {/* Quick link for existing subscribers to manage their plan */}
        <Hint text={HELP.subscription.managePlan} side="bottom" align="end">
          <button
            data-testid="manage-sub-link"
            onClick={() => navigate('/subscription/manage')}
            className="text-xs font-heading text-indigo-600 hover:text-indigo-700 px-3 py-2 rounded-full hover:bg-indigo-50 transition">
            Manage subscription →
          </button>
        </Hint>
      </div>

      {/* Trial / Lifetime Banner */}
      {trialActive && trialBanner && (
        <div data-testid="trial-banner" className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="font-heading text-sm font-bold text-slate-900 inline-flex items-center gap-1">
              Free trial active
              <InfoDot text={HELP.subscription.trialBanner} side="right" testId="hint-trial" />
            </p>
            <p className="text-sm text-slate-700 font-body mt-0.5">{trialBanner}</p>
            {trialEnd && (
              <p className="text-xs text-slate-500 font-body mt-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Trial ends on {trialEnd}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="sub-cycle-toggle">
          {[
            { k: 'yearly', label: 'Annual · 2 months free' },
            { k: 'monthly', label: 'Monthly' },
          ].map(opt => (
            <button key={opt.k} type="button" data-testid={`sub-cycle-${opt.k}`}
              onClick={() => setCycle(opt.k)}
              className={`px-4 py-1.5 rounded-full text-[12px] font-heading transition-all ${cycle === opt.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid gap-6 ${renderableTiers.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : renderableTiers.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
        {renderableTiers.map(([key, tier]) => {
          const style = TIER_STYLES[key] || TIER_STYLES.basic;
          const isCurrent = key === currentTier || (key === 'basic' && isLifetime);
          const IconComp = style.icon;
          const status = tier.tier_status || 'visible';
          const isComingSoon = status === 'coming_soon';
          const planKey = PLAN_KEY_BY_TIER[key];
          const showLifetimeOption = key === 'basic' && tier.lifetime_enabled !== false && !isLifetime;

          return (
            <Card key={key} data-testid={`tier-card-${key}`}
              className={`relative rounded-3xl border-2 ${style.border} overflow-hidden transition-all hover:shadow-lg ${key === 'premium' ? 'md:scale-105 md:shadow-xl' : ''}`}>
              {key === 'premium' && (
                <div data-testid="most-popular-ribbon" className="absolute top-0 inset-x-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center text-xs font-heading py-1.5">
                  Most Popular
                </div>
              )}
              {isComingSoon && key !== 'premium' && (
                <div data-testid={`coming-soon-ribbon-${key}`} className="absolute top-0 inset-x-0 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 text-white text-center text-xs font-heading py-1.5 tracking-wider">
                  COMING SOON
                </div>
              )}
              <div className={`bg-gradient-to-br ${style.gradient} p-6 ${key === 'premium' || isComingSoon ? 'pt-10' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  <IconComp className={`w-6 h-6 ${style.accent}`} />
                  <h3 className={`font-heading text-xl font-bold ${style.accent}`}>{tier.name}</h3>
                </div>
                <div className="mb-4">
                  {tier.price === 0 ? (
                    <p className="font-heading text-3xl font-bold text-slate-900">Free</p>
                  ) : (key === (lifetimeOffer?.tier || 'premium')) ? (
                    <div>
                      <span className="font-heading text-3xl font-bold text-slate-900">₹{Number(lifetimeOffer?.displayPrice ?? 999).toLocaleString('en-IN')}</span>
                      <span className="text-sm text-slate-500 font-body ml-1">/{lifetimeOffer?.displayPeriod || 'month'}</span>
                    </div>
                  ) : (() => {
                    // Headline price follows the Monthly/Annual toggle, falling
                    // back to the cheapest enabled plan when that cycle is off.
                    const plans = tier.billing_plans || {};
                    const picked = plans[cycle];
                    if (picked?.enabled && (picked.price || 0) > 0) {
                      return (
                        <div>
                          <span className="font-heading text-3xl font-bold text-slate-900">₹{(picked.price || 0).toLocaleString('en-IN')}</span>
                          <span className="text-sm text-slate-500 font-body ml-1">{cycle === 'yearly' ? '/year' : '/month'}</span>
                          {cycle === 'yearly' && (
                            <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-heading mt-1">
                              ₹{Math.round((picked.price || 0) / 12).toLocaleString('en-IN')}/month, billed yearly
                            </p>
                          )}
                        </div>
                      );
                    }
                    const enabledPlans = ['monthly', 'yearly', 'half_yearly', 'lifetime']
                      .map(k => ({ k, ...(plans[k] || {}) }))
                      .filter(p => p.enabled && (p.price || 0) > 0);
                    const cheapest = enabledPlans.length
                      ? enabledPlans.reduce((a, b) => (a.price <= b.price ? a : b))
                      : { price: tier.price, label_suffix: '/month' };
                    return (
                      <div>
                        <span className="font-heading text-3xl font-bold text-slate-900">₹{(cheapest.price || 0).toLocaleString('en-IN')}</span>
                        <span className="text-sm text-slate-500 font-body ml-1">{cheapest.label_suffix || '/month'}</span>
                        {enabledPlans.length > 1 && (
                          <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-heading mt-1">
                            {enabledPlans.length} plans available
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* CTA region */}
                {isCurrent ? (
                  <Hint text={HELP.subscription.currentPlan}>
                    <Badge className={`${style.badge} text-xs font-heading`} data-testid={`current-plan-${key}`}>
                      {isLifetime && key === 'basic' ? <><InfinityIcon className="w-3 h-3 mr-1 inline" />Basic Lifetime</> : 'Current Plan'}
                    </Badge>
                  </Hint>
                ) : isComingSoon ? (
                  (() => {
                    const offerHere = lifetimeOffer?.enabled && key === (lifetimeOffer?.tier || 'premium');
                    if (!offerHere) {
                      return (
                        <Hint text={HELP.subscription.comingSoon} className="w-full">
                          <Button data-testid={`coming-soon-btn-${key}`} disabled className="w-full rounded-full font-heading text-sm bg-slate-100 text-slate-500 cursor-default">
                            Notify me at launch
                          </Button>
                        </Hint>
                      );
                    }
                    const ready = !!lifetimeOffer.url;
                    return (
                      <div className="space-y-2">
                        <Button data-testid="lifetime-offer-btn"
                          disabled={!ready}
                          onClick={() => ready && window.open(lifetimeOffer.url, '_blank', 'noopener,noreferrer')}
                          className={`w-full h-auto min-h-[48px] py-3 px-3 rounded-2xl whitespace-normal leading-snug text-center font-heading text-sm bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:shadow-lg text-white ${ready ? '' : 'opacity-60 cursor-not-allowed'}`}>
                          {lifetimeOffer.label || '₹5,999 - Lifetime Offer'}
                        </Button>
                        <p data-testid="lifetime-offer-note" className="text-center text-[12px] leading-snug font-heading text-purple-700">
                          {lifetimeOffer.note}
                        </p>
                      </div>
                    );
                  })()
                ) : key === 'basic' ? (
                  // Basic tier: always free; show "Free Plan" badge if not yet on lifetime
                  <Badge className={`${style.badge} text-xs font-heading`}>Free Plan</Badge>
                ) : (() => {
                  // Hybrid pricing — render one CTA per enabled plan. Plan order
                  // is deliberate: monthly (lowest commitment) → yearly →
                  // lifetime (highest LTV). The lifetime button gets darker
                  // gradient styling so it visually anchors the choice.
                  const plans = tier.billing_plans || {};
                  const tierAlias = key;
                  const planMeta = [
                    { id: 'monthly',  cta: `Subscribe — ${tier.name} Monthly`,    cls: key === 'growth' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white' },
                    { id: 'yearly',   cta: `Pay annually — ${tier.name}`,         cls: 'bg-violet-500 hover:bg-violet-600 text-white' },
                    { id: 'half_yearly', cta: `${tier.name} · 6 months, one time`, cls: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
                    { id: 'lifetime', cta: key === 'growth'
                        ? 'LifeTime Offer · 1st 499 Students · All Upgrades Included'
                        : `Buy ${tier.name} Lifetime — One time`,
                      cls: 'bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600 text-white shadow-md' },
                  ];
                  const enabled = planMeta
                    .map(m => ({ ...m, plan: plans[m.id] }))
                    // Monthly/annual follow the toggle; the one-time offers always show.
                    .filter(({ id }) => (id === 'monthly' || id === 'yearly') ? id === cycle : true)
                    .filter(({ plan }) => plan && plan.enabled && (plan.price || 0) > 0);
                  // Fallback: no plans configured → legacy single monthly CTA
                  if (!enabled.length) {
                    const legacyKey = key === 'premium' ? 'elite_monthly' : 'growth_monthly';
                    return (
                      <Hint text={HELP.subscription.monthly} className="w-full">
                        <Button data-testid={`upgrade-btn-${key}`}
                          className={`w-full rounded-full font-heading text-sm ${key === 'growth' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                          disabled={loadingPlan === legacyKey}
                          onClick={() => startCheckout(legacyKey)}>
                          {loadingPlan === legacyKey ? 'Opening checkout…' : `Upgrade to ${tier.name}`}
                        </Button>
                      </Hint>
                    );
                  }
                  return (
                    <div className="space-y-2" data-testid={`plan-buttons-${key}`}>
                      {enabled.map(({ id, cta, cls, plan }) => {
                        const planKey = `${tierAlias}_${id}`;
                        const priceTxt = `₹${(plan.price || 0).toLocaleString('en-IN')}${(plan.label_suffix || '').trim()}`;
                        return (
                          <Hint key={id} text={PLAN_HELP[id]} className="w-full">
                            <Button
                              data-testid={`upgrade-btn-${key}-${id}`}
                              className={`w-full rounded-full font-heading text-sm justify-between ${cls}`}
                              disabled={loadingPlan === planKey}
                              onClick={() => startCheckout(planKey)}>
                              <span>{loadingPlan === planKey ? 'Opening checkout…' : cta}</span>
                              {loadingPlan !== planKey && (
                                <span className="text-xs opacity-90 font-body">{priceTxt}</span>
                              )}
                            </Button>
                          </Hint>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="p-6 space-y-3">
                {(tier.features || []).map((feature, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.accent}`} />
                    <span className="text-sm font-body text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      {!paymentsOn && (
        <p className="text-xs text-slate-400 font-body mt-6 text-center" data-testid="payments-disabled-notice">
          Live payments not yet enabled. Add Razorpay live keys (RAZORPAY_KEY_ID/SECRET starting with <code>rzp_live_</code>) in your deployment env, or toggle <em>Admin → Feature Toggles → payments_enabled</em>.
        </p>
      )}
    </div>
  );
}
